import React, { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import styled from 'styled-components'
import Text from '@bufferapp/ui/Text'
import { Warning } from '@bufferapp/ui/Icon'
import { AppshellEventKeys } from '@buffer-mono/app-shell'

import {
  useAccount,
  useUserSettings,
  useEngageAccount,
} from '~/modules/account/hooks'
import { Provider as AccountContextProvider } from '~/modules/account/context'
import { AppProvider } from '~/app/providers'
import { Loader } from '~/shared/components'
import { usePrevious } from '~/shared/hooks'
import { mergeChannels } from '~/app/state/utils'
import { useChannels, useCurrentChannel } from '~/app/state/hooks'
import { PostPage } from './pages'
import { PostsProvider } from '~/modules/post/context'
import { Wrapper } from './layout'
import { env } from '~/env'

Bugsnag.start({
  apiKey: '17c534443daf1e1eb231201a373f2ad4',
  plugins: [new BugsnagPluginReact()],
  releaseStage: env.VITE_NODE_ENV,
})

const ErrorBoundaryBugsnag =
  Bugsnag.getPlugin('react').createErrorBoundary(React)

/**
 *
 */
// @ts-expect-error TS(2345) FIXME: Argument of type '() => void' is not assignable to... Remove this comment to see the full error message
const ErrorPage = lazy(() => {
  import(/* webpackChunkName: "error-page" */ './pages/Error/ErrorPage')
})

const ErrorBanner = styled.div`
  height: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  width: 100%;
  label {
    display: flex;
    margin-bottom: 16px;
    svg {
      margin-right: 8px;
    }
  }
`

/**
 *
 */
const FallbackComponent = () => {
  return (
    <ErrorBanner>
      <Text htmlFor="foo" type="help" hasError>
        <Warning />
        Something went wrong.
      </Text>
      <Text htmlFor="foo" type="help" hasError>
        Please reload or contact support if this continues.
      </Text>
    </ErrorBanner>
  )
}

/**
 *
 */
const App = () => {
  const { channels, queryChannels, mutateChannels } = useChannels()

  const { account, queryAccount, queryAccountStatus } = useAccount()

  const {
    engageAccount,
    queryEngageAccount,
    refetchEngageAccount,
    queryEngageAccountStatus,
  } = useEngageAccount()

  const { queryUserSettings, queryUserSettingsStatus } = useUserSettings()

  const { currentChannel, queryCurrentChannel } = useCurrentChannel()

  useEffect(() => {
    queryAccount()
  }, [])

  // @ts-expect-error TS(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
  function handleOrgChange(e) {
    queryAccountStatus.refetch()
  }

  useEffect(() => {
    window.addEventListener(
      AppshellEventKeys.ORGANIZATION_EVENT_KEY,
      handleOrgChange,
    )

    return function cleanup() {
      window.removeEventListener(
        AppshellEventKeys.ORGANIZATION_EVENT_KEY,
        handleOrgChange,
      )
    }
  }, [queryAccountStatus])

  useEffect(() => {
    if (!account?.id) {
      return
    }

    const organization = account.currentOrganization
    const { billing } = organization || {}
    const { canAccessEngagement } = billing || {}

    if (canAccessEngagement) {
      queryEngageAccount({
        variables: {
          organizationId: organization.id,
        },
      })

      queryUserSettings()
      queryCurrentChannel()
    }
  }, [account])

  useEffect(() => {
    if (!account || !engageAccount) return

    const { currentOrganization } = account
    const currentOrganizationChannels = currentOrganization?.channels

    // The main channel data is part of the account information but
    // we are keeping extra info in Engage. The purpose of this
    // is to merge and keep all the data on the same place to make
    // channel management easier.
    const mergedChannels = mergeChannels(
      currentOrganizationChannels,
      engageAccount.channels,
    ).filter((channel) => !channel.isLocked)

    // NOTE: We are currently keeping the list of most frequent
    // used emojis on the engage account data but we are soon
    // going to move those so they are unique for each channel.
    if (engageAccount.emojis) {
      mergedChannels.forEach((channel) => {
        channel.emojis = engageAccount.emojis
      })
    }

    mutateChannels(mergedChannels)
    queryChannels()
  }, [account, engageAccount])

  const prevChannel = usePrevious(currentChannel)
  useEffect(() => {
    // @ts-expect-error TS(2339) FIXME: Property 'serviceId' does not exist on type 'never... Remove this comment to see the full error message
    const sameChannel = currentChannel?.serviceId === prevChannel?.serviceId
    if (sameChannel) {
      return
    }
    refetchEngageAccount()
  }, [currentChannel])

  const displayError = queryUserSettingsStatus.error

  // @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
  const PostPageContent = (props) => (
    <PostsProvider>
      <PostPage error={displayError} {...props} />
    </PostsProvider>
  )

  return (
    <AppProvider>
      <AccountContextProvider value={account}>
        <Wrapper>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*  @ts-ignore */}
          <ErrorBoundaryBugsnag FallbackComponent={FallbackComponent}>
            <Suspense fallback={<Loader delay={300} />}>
              <BrowserRouter>
                <Switch>
                  <Route path="/" render={PostPageContent} exact />
                  <Route path="/:channelId" render={PostPageContent} />
                  <Route
                    path="/:channelId/post/:postId"
                    render={PostPageContent}
                  />
                  <Route path="/post/:postId" render={PostPageContent} />
                  <Route component={ErrorPage} />
                </Switch>
              </BrowserRouter>
            </Suspense>
          </ErrorBoundaryBugsnag>
        </Wrapper>
      </AccountContextProvider>
    </AppProvider>
  )
}

export default App
